import { ProcessedWorkload } from "../../store/healthSlice";

export interface HealthStatus {
  ready: number;
  warning: number;
  error: number;
  total: number;
}

export interface WorkloadMessage {
  type: string;
  location: string;
  version?: number;
  container?: string;
  text: string;
}

export interface HealthIssue {
  id: string;
  severity: "error" | "warning" | "info";
  type: "workload" | "domain" | "quota" | "agent";
  title: string;
  description: string;
  gvc?: string;
  location?: string;
  link?: string;
  timestamp?: string;
  // For workloads - all messages for expandable view
  messages?: WorkloadMessage[];
  workloadName?: string;
  workloadType?: "serverless" | "standard" | "cron" | "stateful";
  expectedVersion?: number;
}

// Issue fix system
export interface IssueFix {
  id: string;
  label: string;
  description: string;
  matcher: (issue: HealthIssue) => boolean;
  // Returns the action to perform - could be a link or a callback
  getAction: (issue: HealthIssue) => {
    type: "link" | "callback";
    link?: string;
    callback?: () => void;
  };
}

// Registry of available fixes
export const ISSUE_FIXES: IssueFix[] = [
  {
    id: "identity-secret-permission",
    label: "Fix",
    description: "Create identity/policy and assign to workload",
    matcher: (issue) => {
      const text = issue.description.toLowerCase();
      return (
        issue.type === "workload" &&
        text.includes("identity") &&
        text.includes("not allowed to reveal the secret")
      );
    },
    getAction: (issue) => {
      // Navigate to identity creation page with context
      const gvc = issue.gvc || "";
      const workloadName = issue.workloadName || "";
      return {
        type: "link",
        link: `/console/org/-/gvc/${gvc}/identity/-create?workload=${workloadName}`,
      };
    },
  },
];

// Find applicable fix for an issue
export const findIssueFix = (issue: HealthIssue): IssueFix | undefined => {
  return ISSUE_FIXES.find((fix) => fix.matcher(issue));
};

export const HEALTH_COLORS = {
  ready: "#10b981", // emerald-500
  warning: "#f59e0b", // amber-500
  error: "#ef4444", // red-500
  inactive: "#6b7280", // gray-500
  suspended: "#8b5cf6", // violet-500
};

export const getWorkloadHealthStatus = (
  processedGvcs: {
    workloads: {
      ready: ProcessedWorkload[];
      notReady: ProcessedWorkload[];
      notLatest: ProcessedWorkload[];
      syncFailed: ProcessedWorkload[];
      suspended: ProcessedWorkload[];
      partiallySuspended: ProcessedWorkload[];
    };
  }[]
): HealthStatus => {
  let ready = 0;
  let warning = 0;
  let error = 0;

  for (const gvc of processedGvcs) {
    ready += gvc.workloads.ready.length + gvc.workloads.suspended.length + gvc.workloads.partiallySuspended.length;
    warning += gvc.workloads.notLatest.length;
    error += gvc.workloads.notReady.length + gvc.workloads.syncFailed.length;
  }

  return { ready, warning, error, total: ready + warning + error };
};

export const getDomainHealthStatus = (
  processedDomains: { error: boolean; warning: boolean }[]
): HealthStatus => {
  let ready = 0;
  let warning = 0;
  let error = 0;

  // Count domains with issues
  for (const domain of processedDomains) {
    if (domain.error) {
      error++;
    } else if (domain.warning) {
      warning++;
    }
  }

  return { ready, warning, error, total: ready + warning + error };
};

export const getQuotaHealthStatus = (
  processedQuotas: { error: boolean; warning: boolean }[]
): HealthStatus => {
  let ready = 0;
  let warning = 0;
  let error = 0;

  for (const quota of processedQuotas) {
    if (quota.error) {
      error++;
    } else if (quota.warning) {
      warning++;
    }
  }

  return { ready, warning, error, total: ready + warning + error };
};

export const getAgentHealthStatus = (
  agents: { name: string }[],
  agentInfoMap: Record<string, any>,
  getIsActive: (agent: any, info: any) => boolean
): { active: number; inactive: number; total: number } => {
  let active = 0;
  let inactive = 0;

  for (const agent of agents) {
    const info = agentInfoMap[agent.name];
    if (getIsActive(agent, info)) {
      active++;
    } else {
      inactive++;
    }
  }

  return { active, inactive, total: active + inactive };
};

export const collectAllIssues = (
  processedGvcs: {
    item: { name: string };
    workloads: {
      notReady: ProcessedWorkload[];
      notLatest: ProcessedWorkload[];
      syncFailed: ProcessedWorkload[];
    };
  }[],
  processedDomains: { name: string; error: boolean; warning: boolean; status: string; text: string }[],
  processedQuotas: { name: string; description: string; gvc: string; remaining: number; error: boolean; warning: boolean }[]
): HealthIssue[] => {
  const issues: HealthIssue[] = [];

  // Collect workload issues
  for (const gvc of processedGvcs) {
    // Sync failed (highest priority)
    for (const workload of gvc.workloads.syncFailed) {
      const mainMessage = workload.messages[0];
      issues.push({
        id: `workload-${workload.selfLink}`,
        severity: "error",
        type: "workload",
        title: `${gvc.item.name}/${workload.item.name}`,
        description: mainMessage?.text || "Sync failed",
        gvc: gvc.item.name,
        location: mainMessage?.location,
        link: `/console${workload.selfLink}`,
        messages: workload.messages as WorkloadMessage[],
        workloadName: workload.item.name,
        workloadType: workload.item.spec?.type,
        expectedVersion: workload.expectedDeploymentVersion,
      });
    }

    // Not ready
    for (const workload of gvc.workloads.notReady) {
      const mainMessage = workload.messages[0];
      issues.push({
        id: `workload-${workload.selfLink}`,
        severity: "error",
        type: "workload",
        title: `${gvc.item.name}/${workload.item.name}`,
        description: mainMessage?.text || "Workload not ready",
        gvc: gvc.item.name,
        location: mainMessage?.location,
        link: `/console${workload.selfLink}`,
        messages: workload.messages as WorkloadMessage[],
        workloadName: workload.item.name,
        workloadType: workload.item.spec?.type,
        expectedVersion: workload.expectedDeploymentVersion,
      });
    }

    // Not latest (warning)
    for (const workload of gvc.workloads.notLatest) {
      const mainMessage = workload.messages[0];
      issues.push({
        id: `workload-${workload.selfLink}`,
        severity: "warning",
        type: "workload",
        title: `${gvc.item.name}/${workload.item.name}`,
        description: mainMessage?.text || "Not running latest version",
        gvc: gvc.item.name,
        location: mainMessage?.location,
        link: `/console${workload.selfLink}`,
        messages: workload.messages as WorkloadMessage[],
        workloadName: workload.item.name,
        workloadType: workload.item.spec?.type,
        expectedVersion: workload.expectedDeploymentVersion,
      });
    }
  }

  // Collect domain issues
  for (const domain of processedDomains) {
    issues.push({
      id: `domain-${domain.name}`,
      severity: domain.error ? "error" : "warning",
      type: "domain",
      title: domain.name,
      description: domain.text || domain.status,
    });
  }

  // Collect quota issues
  for (const quota of processedQuotas) {
    issues.push({
      id: `quota-${quota.name}-${quota.gvc}`,
      severity: quota.error ? "error" : "warning",
      type: "quota",
      title: quota.name,
      description: `${quota.description} - ${quota.remaining} remaining`,
      gvc: quota.gvc,
    });
  }

  // Sort by severity (errors first, then warnings)
  return issues.sort((a, b) => {
    if (a.severity === "error" && b.severity !== "error") return -1;
    if (a.severity !== "error" && b.severity === "error") return 1;
    return 0;
  });
};

export const calculateHealthPercentage = (status: HealthStatus): number => {
  if (status.total === 0) return 100;
  return Math.round((status.ready / status.total) * 100);
};

